/**
 * Chauffeur Shift Page — Module Shift Internes (Phase 1 + Phase 2)
 * Démarrage shift, pauses, changement véhicule, fin de shift avec signature.
 * Phase 2: GPS stop detection popup, expenses with receipt upload, vehicle incidents.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import {
  PlayCircle, StopCircle, PauseCircle, Coffee, Car,
  Clock, Loader2, CheckCircle2, AlertTriangle, Receipt, RefreshCw,
  Pencil, X, Send, MapPin, Camera, ShieldAlert, Upload, Plus
} from 'lucide-react';
import { useShiftGpsStopDetector } from '../../hooks/useShiftGpsStopDetector';
import config from 'config';

const API = config.API_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` });

const EXPENSE_TYPES = [
  { value: 'parking', label: 'Parking' },
  { value: 'pharmacie', label: 'Pharmacie' },
  { value: 'repas_client', label: 'Repas client' },
  { value: 'autre', label: 'Autre' },
];

const GPS_STOP_CLASSIFICATIONS = [
  { value: 'pause_perso', label: 'Pause personnelle', icon: Coffee },
  { value: 'attente_client', label: 'Attente client', icon: Clock },
  { value: 'chargement', label: 'Chargement / Livraison', icon: Car },
  { value: 'autre', label: 'Autre', icon: MapPin },
];

const INCIDENT_TYPES = [
  { value: 'panne', label: 'Panne mécanique' },
  { value: 'accident', label: 'Accident' },
  { value: 'dommage', label: 'Dommage véhicule' },
  { value: 'autre', label: 'Autre' },
];

const STATUS_LABELS = {
  IN_PROGRESS: 'En cours',
  PENDING_VALIDATION: 'En attente validation',
  VALIDATED: 'Validé',
  CORRECTED: 'Corrigé',
  FLAGGED: 'Anomalie détectée',
};

const fmtDuration = (mins) => {
  if (!mins || mins <= 0) return '0h00';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h${String(m).padStart(2, '0')}`;
};

const fmtTime = (iso) => {
  if (!iso) return '--:--';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '--:--'; }
};

const fmtDate = (iso) => {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '--'; }
};

export default function ChauffeurShiftPage() {
  const [shift, setShift] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('current'); // current | history

  // Start shift form
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [kmStart, setKmStart] = useState('');

  // End shift form
  const [showEndForm, setShowEndForm] = useState(false);
  const [kmEnd, setKmEnd] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [newExpType, setNewExpType] = useState('parking');
  const [newExpAmount, setNewExpAmount] = useState('');
  const [newExpDesc, setNewExpDesc] = useState('');
  const sigRef = useRef(null);

  // Vehicle change
  const [showVehicleChange, setShowVehicleChange] = useState(false);
  const [vcKmOut, setVcKmOut] = useState('');
  const [vcNewVehicle, setVcNewVehicle] = useState('');
  const [vcKmIn, setVcKmIn] = useState('');

  // Phase 2: Incidents
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incType, setIncType] = useState('panne');
  const [incDesc, setIncDesc] = useState('');
  const [incSeverity, setIncSeverity] = useState('medium');
  const [incPhoto, setIncPhoto] = useState(null);

  // Phase 2: Expense receipt (for inline add during shift)
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expReceipt, setExpReceipt] = useState(null);

  // Phase 2: GPS stop classification note
  const [stopNote, setStopNote] = useState('');

  // Pause state
  const isPaused = shift?.pauses?.length > 0 && !shift.pauses[shift.pauses.length - 1]?.ended_at;

  // Phase 2: GPS Stop Detector
  const { pendingStop, classifyStop, detecting } = useShiftGpsStopDetector(
    !!(shift && shift.status === 'IN_PROGRESS' && !isPaused)
  );

  const fetchCurrent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/chauffeur/shifts/current`, { headers: getHeaders() });
      setShift(data.shift);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/chauffeur/shifts/vehicles`, { headers: getHeaders() });
      setVehicles(data.vehicles || []);
    } catch { /* silent */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/chauffeur/shifts/history`, { headers: getHeaders() });
      setHistory(data.shifts || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCurrent();
    fetchVehicles();
  }, [fetchCurrent, fetchVehicles]);

  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab, fetchHistory]);

  // Auto-refresh current shift every 60s
  useEffect(() => {
    if (!shift || shift.status !== 'IN_PROGRESS') return;
    const interval = setInterval(fetchCurrent, 60000);
    return () => clearInterval(interval);
  }, [shift, fetchCurrent]);

  const handleStartShift = async () => {
    if (!selectedVehicle || !kmStart) return;
    setActionLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/chauffeur/shifts/start`, {
        vehicle_id: selectedVehicle,
        km_start: parseFloat(kmStart),
      }, { headers: getHeaders() });
      setShift(data.shift);
      setKmStart('');
      setSelectedVehicle('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du démarrage');
    }
    setActionLoading(false);
  };

  const handleTogglePause = async () => {
    setActionLoading(true);
    try {
      if (isPaused) {
        await axios.post(`${API}/api/chauffeur/shifts/current/pause/end`, {}, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/api/chauffeur/shifts/current/pause/start`, {}, { headers: getHeaders() });
      }
      await fetchCurrent();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setActionLoading(false);
  };

  const handleVehicleChange = async () => {
    if (!vcKmOut || !vcNewVehicle || !vcKmIn) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/api/chauffeur/shifts/current/vehicle-change`, {
        km_out: parseFloat(vcKmOut),
        new_vehicle_id: vcNewVehicle,
        km_in: parseFloat(vcKmIn),
      }, { headers: getHeaders() });
      setShowVehicleChange(false);
      setVcKmOut(''); setVcNewVehicle(''); setVcKmIn('');
      await fetchCurrent();
      await fetchVehicles();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setActionLoading(false);
  };

  const addExpense = () => {
    if (!newExpAmount) return;
    setExpenses(prev => [...prev, { type: newExpType, amount: parseFloat(newExpAmount), description: newExpDesc }]);
    setNewExpAmount(''); setNewExpDesc('');
  };

  const handleEndShift = async () => {
    if (!kmEnd) { alert('Saisissez le kilométrage de fin.'); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { alert('La signature est obligatoire.'); return; }

    setActionLoading(true);
    try {
      const signatureData = sigRef.current.toDataURL('image/png');
      const { data } = await axios.post(`${API}/api/chauffeur/shifts/current/end`, {
        km_end: parseFloat(kmEnd),
        expenses,
        signature_data: signatureData,
      }, { headers: getHeaders() });
      setShift(null);
      setShowEndForm(false);
      setKmEnd(''); setExpenses([]);
      alert(data.message || 'Shift terminé !');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
    setActionLoading(false);
  };

  // Phase 2: Add expense with receipt upload during active shift
  const handleAddExpenseInline = async () => {
    if (!newExpAmount) return;
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('expense_type', newExpType);
      formData.append('amount', parseFloat(newExpAmount));
      formData.append('description', newExpDesc);
      if (expReceipt) formData.append('receipt', expReceipt);

      await axios.post(`${API}/api/chauffeur/shifts/current/expense`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setNewExpAmount(''); setNewExpDesc(''); setExpReceipt(null);
      setShowAddExpense(false);
      await fetchCurrent();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur ajout dépense');
    }
    setActionLoading(false);
  };

  // Phase 2: Report vehicle incident
  const handleReportIncident = async () => {
    if (!incDesc) { alert('Décrivez l\'incident.'); return; }
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('incident_type', incType);
      formData.append('description', incDesc);
      formData.append('severity', incSeverity);
      if (incPhoto) formData.append('photo', incPhoto);

      await axios.post(`${API}/api/chauffeur/shifts/current/incident`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setShowIncidentForm(false);
      setIncType('panne'); setIncDesc(''); setIncSeverity('medium'); setIncPhoto(null);
      await fetchCurrent();
      alert('Incident signalé.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur signalement incident');
    }
    setActionLoading(false);
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
    </div>
  );

  return (
    <div className="p-4 pb-24 max-w-lg mx-auto" data-testid="chauffeur-shift-page">
      {/* Phase 2: GPS Stop Classification Popup — Mandatory, blocks UI */}
      {pendingStop && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="gps-stop-popup">
          <Card className="bg-[#1A1A1A] border-amber-500/30 w-full max-w-sm">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Arrêt détecté</h3>
                  <p className="text-white/40 text-xs">Vous êtes immobile depuis 5+ minutes</p>
                </div>
              </div>
              <p className="text-white/60 text-xs">Veuillez indiquer la raison de cet arrêt :</p>
              <div className="grid grid-cols-2 gap-2">
                {GPS_STOP_CLASSIFICATIONS.map(cls => {
                  const Icon = cls.icon;
                  return (
                    <button key={cls.value}
                      onClick={() => classifyStop(pendingStop.stop_id, cls.value, stopNote)}
                      className="flex flex-col items-center gap-1.5 p-3 bg-white/5 rounded-xl border border-white/10 hover:border-amber-500/30 hover:bg-amber-500/5 transition"
                      data-testid={`classify-${cls.value}`}>
                      <Icon className="w-5 h-5 text-amber-400" />
                      <span className="text-white text-xs font-medium text-center leading-tight">{cls.label}</span>
                    </button>
                  );
                })}
              </div>
              <input
                value={stopNote}
                onChange={e => setStopNote(e.target.value)}
                placeholder="Note optionnelle..."
                className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                data-testid="stop-note-input"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* GPS detection indicator */}
      {detecting && shift?.status === 'IN_PROGRESS' && (
        <div className="flex items-center gap-1.5 mb-2 px-2 py-1 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-emerald-400/60 text-[10px]">Détection GPS arrêt active</span>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'current', label: 'Shift en cours' },
          { key: 'history', label: 'Historique' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2 rounded-xl text-sm font-medium transition ${
              tab === t.key ? 'bg-[#C9A24D] text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
            data-testid={`shift-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'current' && (
        <>
          {/* No active shift — Start form */}
          {!shift && (
            <Card className="bg-[#1A1A1A] border-white/5" data-testid="start-shift-form">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <PlayCircle className="w-5 h-5 text-[#C9A24D]" />
                  <h2 className="text-white font-semibold text-lg">Démarrer un shift</h2>
                </div>

                {/* Vehicle selection */}
                <div>
                  <label className="text-white/60 text-xs mb-1 block">Véhicule</label>
                  <select
                    value={selectedVehicle}
                    onChange={(e) => setSelectedVehicle(e.target.value)}
                    className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
                    data-testid="vehicle-select"
                  >
                    <option value="">Sélectionner un véhicule</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id} disabled={v.in_use}>
                        {v.name} — {v.license_plate} {v.in_use ? '(En utilisation)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Km start */}
                <div>
                  <label className="text-white/60 text-xs mb-1 block">Km départ</label>
                  <input
                    type="number"
                    value={kmStart}
                    onChange={e => setKmStart(e.target.value)}
                    placeholder="Ex: 15230"
                    className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
                    data-testid="km-start-input"
                  />
                </div>

                <Button
                  onClick={handleStartShift}
                  disabled={!selectedVehicle || !kmStart || actionLoading}
                  className="w-full bg-[#C9A24D] hover:bg-[#b8923e] text-black font-semibold"
                  data-testid="start-shift-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlayCircle className="w-4 h-4 mr-2" />Démarrer le shift</>}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Active shift */}
          {shift && shift.status === 'IN_PROGRESS' && (
            <div className="space-y-3" data-testid="active-shift">
              {/* Status card */}
              <Card className={`border ${isPaused ? 'bg-amber-500/5 border-amber-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full animate-pulse ${isPaused ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                      <span className={`text-sm font-semibold ${isPaused ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {isPaused ? 'EN PAUSE' : 'SHIFT EN COURS'}
                      </span>
                    </div>
                    <span className="text-white/40 text-xs">{fmtTime(shift.started_at)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-white/40 text-[10px]">Amplitude</p>
                      <p className="text-white font-bold text-lg" data-testid="amplitude">{fmtDuration(shift.amplitude_minutes)}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[10px]">Pause</p>
                      <p className="text-amber-400 font-bold text-lg" data-testid="pause-time">{fmtDuration(shift.pause_minutes)}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[10px]">Travail</p>
                      <p className="text-emerald-400 font-bold text-lg" data-testid="work-time">{fmtDuration(shift.work_minutes)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Vehicle info */}
              <Card className="bg-[#1A1A1A] border-white/5">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-[#C9A24D]" />
                    <div>
                      <p className="text-white text-sm font-medium">{shift.vehicle_name}</p>
                      <p className="text-white/40 text-xs">{shift.vehicle_plate} — Km départ: {shift.km_start}</p>
                    </div>
                  </div>
                  <button onClick={() => { setShowVehicleChange(!showVehicleChange); setShowEndForm(false); }}
                    className="text-[#C9A24D] text-xs hover:underline" data-testid="change-vehicle-btn">
                    <RefreshCw className="w-3.5 h-3.5 inline mr-1" />Changer
                  </button>
                </CardContent>
              </Card>

              {/* Vehicle change form */}
              {showVehicleChange && (
                <Card className="bg-[#1A1A1A] border-[#C9A24D]/20" data-testid="vehicle-change-form">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-[#C9A24D]" />Changement de véhicule
                    </h3>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block">Km sortie (véhicule actuel)</label>
                      <input type="number" value={vcKmOut} onChange={e => setVcKmOut(e.target.value)}
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
                        data-testid="vc-km-out" />
                    </div>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block">Nouveau véhicule</label>
                      <select value={vcNewVehicle} onChange={e => setVcNewVehicle(e.target.value)}
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
                        data-testid="vc-new-vehicle">
                        <option value="">Sélectionner</option>
                        {vehicles.filter(v => v.id !== shift.vehicle_id && !v.in_use).map(v => (
                          <option key={v.id} value={v.id}>{v.name} — {v.license_plate}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block">Km entrée (nouveau véhicule)</label>
                      <input type="number" value={vcKmIn} onChange={e => setVcKmIn(e.target.value)}
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
                        data-testid="vc-km-in" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleVehicleChange} disabled={actionLoading || !vcKmOut || !vcNewVehicle || !vcKmIn}
                        className="flex-1 bg-[#C9A24D] hover:bg-[#b8923e] text-black text-sm" data-testid="vc-confirm-btn">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
                      </Button>
                      <Button onClick={() => setShowVehicleChange(false)} variant="outline"
                        className="border-white/10 text-white/60 text-sm" data-testid="vc-cancel-btn">
                        Annuler
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button onClick={handleTogglePause} disabled={actionLoading}
                  className={`flex-1 text-sm font-semibold ${isPaused
                    ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20'
                    : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20'
                  }`}
                  data-testid="toggle-pause-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    isPaused ? <><PlayCircle className="w-4 h-4 mr-1.5" />Reprendre</> :
                    <><PauseCircle className="w-4 h-4 mr-1.5" />Pause</>
                  }
                </Button>
                <Button onClick={() => { setShowEndForm(!showEndForm); setShowVehicleChange(false); }}
                  disabled={actionLoading}
                  className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 text-sm font-semibold"
                  data-testid="end-shift-toggle-btn">
                  <StopCircle className="w-4 h-4 mr-1.5" />Terminer shift
                </Button>
              </div>

              {/* Phase 2: Quick action buttons row */}
              <div className="flex gap-2">
                <button onClick={() => { setShowAddExpense(!showAddExpense); setShowIncidentForm(false); setShowVehicleChange(false); setShowEndForm(false); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#C9A24D]/10 text-[#C9A24D] text-xs font-medium border border-[#C9A24D]/20 hover:bg-[#C9A24D]/20 transition"
                  data-testid="add-expense-btn">
                  <Receipt className="w-3.5 h-3.5" />Dépense
                </button>
                <button onClick={() => { setShowIncidentForm(!showIncidentForm); setShowAddExpense(false); setShowVehicleChange(false); setShowEndForm(false); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-500/10 text-red-400 text-xs font-medium border border-red-500/20 hover:bg-red-500/20 transition"
                  data-testid="report-incident-btn">
                  <ShieldAlert className="w-3.5 h-3.5" />Incident
                </button>
              </div>

              {/* Phase 2: Add Expense inline form (with receipt upload) */}
              {showAddExpense && (
                <Card className="bg-[#1A1A1A] border-[#C9A24D]/20" data-testid="inline-expense-form">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-[#C9A24D]" />Ajouter une dépense
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-white/60 text-xs mb-1 block">Type</label>
                        <select value={newExpType} onChange={e => setNewExpType(e.target.value)}
                          className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none">
                          {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-white/60 text-xs mb-1 block">Montant</label>
                        <input type="number" value={newExpAmount} onChange={e => setNewExpAmount(e.target.value)}
                          placeholder="0.00" step="0.01"
                          className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block">Description</label>
                      <input value={newExpDesc} onChange={e => setNewExpDesc(e.target.value)}
                        placeholder="Détails..."
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block flex items-center gap-1">
                        <Camera className="w-3 h-3" /> Justificatif (photo/scan)
                      </label>
                      <label className="flex items-center gap-2 px-3 py-2 bg-[#111] border border-white/10 rounded-lg cursor-pointer hover:border-[#C9A24D]/30 transition">
                        <Upload className="w-4 h-4 text-white/40" />
                        <span className="text-white/60 text-sm truncate">
                          {expReceipt ? expReceipt.name : 'Choisir un fichier...'}
                        </span>
                        <input type="file" accept="image/*,application/pdf" className="hidden"
                          onChange={e => setExpReceipt(e.target.files[0] || null)}
                          data-testid="expense-receipt-input" />
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleAddExpenseInline} disabled={actionLoading || !newExpAmount}
                        className="flex-1 bg-[#C9A24D] hover:bg-[#b8923e] text-black text-sm" data-testid="submit-expense-btn">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Ajouter</>}
                      </Button>
                      <Button onClick={() => setShowAddExpense(false)} variant="outline"
                        className="border-white/10 text-white/60 text-sm">Annuler</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Phase 2: Incident Report form */}
              {showIncidentForm && (
                <Card className="bg-[#1A1A1A] border-red-500/20" data-testid="incident-form">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-red-400" />Signaler un incident
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-white/60 text-xs mb-1 block">Type</label>
                        <select value={incType} onChange={e => setIncType(e.target.value)}
                          className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none">
                          {INCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-white/60 text-xs mb-1 block">Gravité</label>
                        <select value={incSeverity} onChange={e => setIncSeverity(e.target.value)}
                          className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none">
                          <option value="low">Faible</option>
                          <option value="medium">Moyenne</option>
                          <option value="high">Grave</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block">Description</label>
                      <textarea value={incDesc} onChange={e => setIncDesc(e.target.value)}
                        rows={3} placeholder="Décrivez l'incident..."
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-2.5 py-2 text-white text-sm focus:outline-none resize-none" />
                    </div>
                    <div>
                      <label className="text-white/60 text-xs mb-1 block flex items-center gap-1">
                        <Camera className="w-3 h-3" /> Photo
                      </label>
                      <label className="flex items-center gap-2 px-3 py-2 bg-[#111] border border-white/10 rounded-lg cursor-pointer hover:border-red-500/30 transition">
                        <Camera className="w-4 h-4 text-white/40" />
                        <span className="text-white/60 text-sm truncate">
                          {incPhoto ? incPhoto.name : 'Prendre / choisir une photo...'}
                        </span>
                        <input type="file" accept="image/*" capture="environment" className="hidden"
                          onChange={e => setIncPhoto(e.target.files[0] || null)}
                          data-testid="incident-photo-input" />
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleReportIncident} disabled={actionLoading || !incDesc}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm" data-testid="submit-incident-btn">
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldAlert className="w-4 h-4 mr-1" />Signaler</>}
                      </Button>
                      <Button onClick={() => setShowIncidentForm(false)} variant="outline"
                        className="border-white/10 text-white/60 text-sm">Annuler</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Phase 2: Show registered expenses during shift */}
              {shift.expenses?.length > 0 && (
                <Card className="bg-[#1A1A1A] border-white/5">
                  <CardContent className="p-3">
                    <p className="text-white/40 text-xs font-medium mb-2 flex items-center gap-1">
                      <Receipt className="w-3 h-3" /> Dépenses ({shift.expenses.length})
                    </p>
                    {shift.expenses.map((exp, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-white/60">{EXPENSE_TYPES.find(t => t.value === exp.type)?.label || exp.type}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[#C9A24D] font-semibold">{exp.amount?.toFixed(2)} EUR</span>
                          {exp.receipt_path && <Camera className="w-3 h-3 text-emerald-400" title="Justificatif attaché" />}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Phase 2: Show registered incidents during shift */}
              {shift.incidents?.length > 0 && (
                <Card className="bg-[#1A1A1A] border-red-500/10">
                  <CardContent className="p-3">
                    <p className="text-red-400/60 text-xs font-medium mb-2 flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> Incidents ({shift.incidents.length})
                    </p>
                    {shift.incidents.map((inc, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-white/60">
                          {INCIDENT_TYPES.find(t => t.value === inc.type)?.label || inc.type}
                          {inc.severity === 'high' && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-400" />}
                        </span>
                        <span className="text-white/30 text-[10px]">{fmtTime(inc.reported_at)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Pauses list */}
              {shift.pauses?.length > 0 && (
                <Card className="bg-[#1A1A1A] border-white/5">
                  <CardContent className="p-3">
                    <p className="text-white/40 text-xs font-medium mb-2 flex items-center gap-1">
                      <Coffee className="w-3 h-3" /> Pauses ({shift.pauses.length})
                    </p>
                    {shift.pauses.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-white/60">
                          {fmtTime(p.started_at)} → {p.ended_at ? fmtTime(p.ended_at) : <span className="text-amber-400">en cours</span>}
                        </span>
                        {p.reason && <span className="text-white/30 italic">{p.reason}</span>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* End shift form */}
              {showEndForm && (
                <Card className="bg-[#1A1A1A] border-red-500/20" data-testid="end-shift-form">
                  <CardContent className="p-4 space-y-4">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                      <StopCircle className="w-4 h-4 text-red-400" />Fin de shift
                    </h3>

                    {/* Km end */}
                    <div>
                      <label className="text-white/60 text-xs mb-1 block">Km fin</label>
                      <input type="number" value={kmEnd} onChange={e => setKmEnd(e.target.value)}
                        placeholder={`Min: ${shift.km_start}`}
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
                        data-testid="km-end-input" />
                    </div>

                    {/* Expenses */}
                    <div>
                      <label className="text-white/60 text-xs mb-2 block flex items-center gap-1">
                        <Receipt className="w-3 h-3" /> Dépenses
                      </label>
                      {expenses.map((exp, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#111] rounded-lg px-3 py-2 mb-1.5 text-xs">
                          <span className="text-white">{EXPENSE_TYPES.find(t => t.value === exp.type)?.label || exp.type}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-[#C9A24D] font-semibold">{exp.amount.toFixed(2)} EUR</span>
                            <button onClick={() => setExpenses(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-1.5 items-end">
                        <select value={newExpType} onChange={e => setNewExpType(e.target.value)}
                          className="bg-[#111] border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none flex-1">
                          {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input type="number" value={newExpAmount} onChange={e => setNewExpAmount(e.target.value)}
                          placeholder="Montant" step="0.01"
                          className="bg-[#111] border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none w-20" />
                        <button onClick={addExpense} disabled={!newExpAmount}
                          className="bg-[#C9A24D]/20 text-[#C9A24D] rounded-lg px-2 py-2 text-xs hover:bg-[#C9A24D]/30 disabled:opacity-40">
                          +
                        </button>
                      </div>
                    </div>

                    {/* Signature */}
                    <div>
                      <label className="text-white/60 text-xs mb-2 block flex items-center gap-1">
                        <Pencil className="w-3 h-3" /> Signature obligatoire
                      </label>
                      <div className="bg-white rounded-lg overflow-hidden relative" data-testid="signature-pad-container">
                        <SignatureCanvas
                          ref={sigRef}
                          penColor="black"
                          canvasProps={{
                            className: 'w-full',
                            style: { width: '100%', height: '150px' },
                            'data-testid': 'signature-canvas'
                          }}
                        />
                        <button
                          onClick={() => sigRef.current?.clear()}
                          className="absolute top-1 right-1 bg-gray-200 rounded-full p-1 hover:bg-gray-300"
                          data-testid="clear-signature-btn"
                        >
                          <X className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                      <p className="text-white/30 text-[10px] mt-1">Signez dans le cadre blanc ci-dessus</p>
                    </div>

                    {/* Submit */}
                    <Button onClick={handleEndShift} disabled={actionLoading || !kmEnd}
                      className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold"
                      data-testid="confirm-end-shift-btn">
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                        <><Send className="w-4 h-4 mr-2" />Confirmer la fin de shift</>}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* No active shift and no form shown yet */}
          {!shift && vehicles.length === 0 && (
            <div className="text-center py-12 text-white/40">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              <p className="text-sm">Aucun véhicule interne disponible.</p>
            </div>
          )}
        </>
      )}

      {/* History tab */}
      {tab === 'history' && (
        <div className="space-y-2" data-testid="shift-history">
          {history.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <Clock className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Aucun shift terminé.</p>
            </div>
          ) : (
            history.map((s, i) => (
              <Card key={s.id || i} className="bg-[#1A1A1A] border-white/5">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm font-medium">{fmtDate(s.started_at)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      s.status === 'VALIDATED' ? 'bg-emerald-500/10 text-emerald-400' :
                      s.status === 'FLAGGED' ? 'bg-red-500/10 text-red-400' :
                      s.status === 'CORRECTED' ? 'bg-blue-500/10 text-blue-400' :
                      'bg-amber-500/10 text-amber-400'
                    }`} data-testid={`shift-status-${i}`}>
                      {STATUS_LABELS[s.status] || s.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <p className="text-white/40">Amplitude</p>
                      <p className="text-white font-semibold">{fmtDuration(s.amplitude_minutes)}</p>
                    </div>
                    <div>
                      <p className="text-white/40">Pause</p>
                      <p className="text-amber-400 font-semibold">{fmtDuration(s.pause_minutes)}</p>
                    </div>
                    <div>
                      <p className="text-white/40">Travail</p>
                      <p className="text-emerald-400 font-semibold">{fmtDuration(s.work_minutes)}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[10px] text-white/30">
                    <span>{s.vehicle_name} — {s.vehicle_plate}</span>
                    <span>{s.km_start} → {s.km_end} km</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
